import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Hook para transcripción de voz a texto usando la Web Speech API del browser.
 * Nada pasa por servidor — todo ocurre localmente. No tiene costo.
 *
 * Soporte: Chrome, Edge, Safari. Firefox no lo implementa (2026).
 * Si no hay soporte, `isSupported` es false y el consumidor debería ocultar la UI.
 */

// Tipos de la Web Speech API (no están en el standard lib de TS todavía)
interface SpeechRecognitionResultLike {
  0: { transcript: string };
  isFinal: boolean;
}
interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: {
    length: number;
    [index: number]: SpeechRecognitionResultLike;
  };
}
interface SpeechRecognitionErrorEventLike {
  error: string;
  message?: string;
}
interface SpeechRecognitionInstance {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onstart: (() => void) | null;
  onend:   (() => void) | null;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror:  ((e: SpeechRecognitionErrorEventLike) => void) | null;
  start: () => void;
  stop:  () => void;
  abort: () => void;
}

function getRecognitionCtor(): (new () => SpeechRecognitionInstance) | null {
  if (typeof window === 'undefined') return null;
  return (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition || null;
}

/** Une dos fragmentos con un espacio, ignorando vacíos. */
function join(a: string, b: string): string {
  return a && b ? `${a} ${b}` : a || b;
}

/**
 * Une dos textos colapsando el SOLAPAMIENTO (sufijo de `a` == prefijo de `b`),
 * comparando por palabras (case-insensitive). Resuelve el caso del navegador que,
 * al reiniciar el reconocimiento, re-captura audio ya dicho y lo duplicaba:
 *   mergeOverlap("cómo estás", "cómo estás Gustavo") => "cómo estás Gustavo"
 */
function mergeOverlap(a: string, b: string): string {
  const aT = a.trim();
  const bT = b.trim();
  if (!aT) return bT;
  if (!bT) return aT;
  const aw = aT.split(/\s+/);
  const bw = bT.split(/\s+/);
  const max = Math.min(aw.length, bw.length);
  let overlap = 0;
  for (let k = max; k > 0; k--) {
    if (aw.slice(aw.length - k).join(' ').toLowerCase() === bw.slice(0, k).join(' ').toLowerCase()) {
      overlap = k;
      break;
    }
  }
  return [...aw, ...bw.slice(overlap)].join(' ');
}

/**
 * Red de seguridad: colapsa repeticiones CONSECUTIVAS de n-gramas (n=5..1).
 *   "quiero quiero desestimar al contacto" => "quiero desestimar al contacto"
 */
function collapseRepeats(text: string): string {
  if (!text) return text;
  let words = text.trim().split(/\s+/).filter(Boolean);
  for (let n = 5; n >= 1; n--) {
    const out: string[] = [];
    let i = 0;
    while (i < words.length) {
      const cur = words.slice(i, i + n);
      out.push(...cur);
      i += n;
      const key = cur.join(' ').toLowerCase();
      while (cur.length === n && i + n <= words.length && words.slice(i, i + n).join(' ').toLowerCase() === key) {
        i += n; // saltar la repetición consecutiva del mismo n-grama
      }
    }
    words = out;
  }
  return words.join(' ');
}

interface UseSpeechRecognitionOptions {
  lang?: string;                             // default 'es-UY'
  onFinalTranscript?: (text: string) => void; // se dispara cuando el user para
  onInterim?: (text: string) => void;        // mientras habla
}

export function useSpeechRecognition(options: UseSpeechRecognitionOptions = {}) {
  const { lang = 'es-UY', onFinalTranscript, onInterim } = options;

  const Ctor = getRecognitionCtor();
  const isSupported = !!Ctor;

  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);

  // El transcript se arma en dos capas para que el dictado NO se duplique:
  //  - committedRef: finales de sesiones ya cerradas por auto-restart (ver onend).
  //  - sessionFinalRef: final de la sesión ACTIVA, recalculado de cero en cada
  //    onresult (idempotente). Nunca acumulamos con += sobre eventos, porque el
  //    browser re-entrega resultados ya emitidos y eso repetía el texto N veces.
  const committedRef = useRef<string>('');
  const sessionFinalRef = useRef<string>('');

  // Cuando el user pulsa stop/cancel, marcamos este ref. El onend lo lee para
  // decidir si reiniciar (sesión activa terminada por silencio del browser) o
  // terminar definitivamente (user pidió parar). Sin esto, el browser corta la
  // grabación al primer silencio aunque continuous=true.
  const userStoppedRef = useRef<boolean>(true);

  // Reintentos ante error 'network' (corte puntual de red, común en mobile).
  // Reintentamos en silencio 1 vez antes de mostrarle el error al agente.
  const networkRetriesRef = useRef<number>(0);

  const onFinalRef = useRef(onFinalTranscript);
  const onInterimRef = useRef(onInterim);
  useEffect(() => { onFinalRef.current = onFinalTranscript; }, [onFinalTranscript]);
  useEffect(() => { onInterimRef.current = onInterim; }, [onInterim]);

  // Ref a la función que levanta una sesión nueva. Lo usamos en onend para
  // reiniciar con una instancia FRESCA (no reusar el objeto), evitando que el
  // engine arrastre el último final de la sesión cerrada y lo duplique.
  const spawnRef = useRef<(() => void) | null>(null);

  const buildRecognition = useCallback(() => {
    if (!Ctor) return null;
    const r = new Ctor();
    r.lang = lang;
    // continuous=true mantiene la sesión abierta aunque haya pausas; el agente
    // controla cuándo terminar con stop()/cancel().
    r.continuous = true;
    r.interimResults = true;
    r.maxAlternatives = 1;

    r.onstart = () => {
      setIsListening(true);
    };
    r.onend = () => {
      // Al cerrar la sesión, fijamos su final en committedRef. La próxima sesión
      // arranca limpia (sessionFinalRef = '') y su onresult solo aporta texto
      // nuevo, así nunca se reprocesa lo ya dicho.
      if (sessionFinalRef.current) {
        // mergeOverlap (no join) para descartar lo que la sesión nueva re-capturó.
        committedRef.current = mergeOverlap(committedRef.current, sessionFinalRef.current);
        sessionFinalRef.current = '';
      }
      // Si el user no pidió parar, reiniciamos: algunos browsers (Chrome ~60s
      // de silencio, Safari aún más agresivo) cortan igual y necesitamos
      // levantar una sesión nueva para que no parezca que se pausó solo.
      if (!userStoppedRef.current) {
        spawnRef.current?.();
        return;
      }
      setIsListening(false);
      const finalText = collapseRepeats(committedRef.current).trim();
      if (finalText) {
        onFinalRef.current?.(finalText);
      }
    };
    r.onresult = (e) => {
      let interim = '';
      let final = '';
      // Recorremos TODO el array de la sesión actual (no desde resultIndex):
      // recalcular de cero es idempotente y evita la doble acumulación.
      for (let i = 0; i < e.results.length; i++) {
        const res = e.results[i];
        const text = res[0]?.transcript ?? '';
        // mergeOverlap entre finales consecutivos (el engine a veces re-emite
        // finales solapados dentro de la misma sesión).
        if (res.isFinal) final = mergeOverlap(final, text.trim());
        else             interim += text;
      }
      sessionFinalRef.current = final;
      // Para el display en vivo: fusionamos committed+final por solapamiento y
      // colapsamos repeticiones residuales antes de mostrar.
      const base = mergeOverlap(committedRef.current, final);
      onInterimRef.current?.(collapseRepeats(join(base, interim)).trim());
    };
    r.onerror = (e) => {
      // no-speech / aborted: el browser cortó por silencio o restart — no es
      // un error real, dejamos que onend decida si reiniciar.
      if (e.error === 'no-speech' || e.error === 'aborted') return;

      // network: corte puntual de red. Reintentamos en silencio 1 vez (no
      // marcamos stop ni error → onend respawnea la sesión). Si vuelve a fallar,
      // recién ahí mostramos el mensaje al agente.
      if (e.error === 'network' && !userStoppedRef.current && networkRetriesRef.current < 1) {
        networkRetriesRef.current += 1;
        return;
      }

      const msg = e.error === 'not-allowed' || e.error === 'service-not-allowed'
        ? 'Permiso de micrófono denegado. Activá el micrófono en la configuración del sitio.'
        : e.error === 'audio-capture'
          ? 'No se pudo acceder al micrófono.'
          : e.error === 'network'
            ? 'Se cortó la conexión del reconocimiento de voz. Revisá tu internet e intentá de nuevo.'
            : `Error de reconocimiento: ${e.error}`;
      setError(msg);
      userStoppedRef.current = true;  // error real → no reiniciar
      setIsListening(false);
    };
    return r;
  }, [Ctor, lang]);

  // Levanta una instancia nueva y la arranca. Usada tanto por start() como por
  // el auto-restart en onend.
  const spawn = useCallback(() => {
    const r = buildRecognition();
    if (!r) return;
    recognitionRef.current = r;
    try {
      r.start();
    } catch {
      // A veces start() tira si se llamó demasiado rápido; ignorar.
    }
  }, [buildRecognition]);
  useEffect(() => { spawnRef.current = spawn; }, [spawn]);

  const start = useCallback(() => {
    if (!Ctor) {
      setError('Tu navegador no soporta reconocimiento de voz. Usá Chrome, Edge o Safari.');
      return;
    }
    setError(null);
    committedRef.current = '';
    sessionFinalRef.current = '';
    userStoppedRef.current = false;
    networkRetriesRef.current = 0;
    spawn();
  }, [Ctor, spawn]);

  const stop = useCallback(() => {
    userStoppedRef.current = true;
    try { recognitionRef.current?.stop(); } catch { /* noop */ }
  }, []);

  const cancel = useCallback(() => {
    userStoppedRef.current = true;
    try { recognitionRef.current?.abort(); } catch { /* noop */ }
    committedRef.current = '';
    sessionFinalRef.current = '';
    setIsListening(false);
  }, []);

  useEffect(() => {
    return () => {
      try { recognitionRef.current?.abort(); } catch { /* noop */ }
    };
  }, []);

  return { isSupported, isListening, error, start, stop, cancel };
}
